import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useAuth } from "./auth";

type OrgaoContextType = {
    currentOrgaoId: number | null;
    setCurrentOrgaoId: (orgaoId: number | null) => void;
};

const OrgaoContext = createContext<OrgaoContextType>({
    currentOrgaoId: null,
    setCurrentOrgaoId: () => { },
});

export default function OrgaoProvider({ children }: { children: React.ReactNode }) {
    const { adminUser } = useAuth();
    const [currentOrgaoId, setCurrentOrgaoIdState] = useState<number | null>(null);

    const loadCurrentOrgao = useCallback(() => {
        const savedOrgaoId = localStorage.getItem('current_orgao_id');
        
        if (savedOrgaoId) {
            const orgaoId = Number(savedOrgaoId);
            if (!isNaN(orgaoId)) {
                setCurrentOrgaoIdState(orgaoId);
                return;
            }
        }

        // Fallback: usar órgão do adminUser
        if (adminUser) {
            const defaultOrgaoId = adminUser.orgao_usuario?.id || adminUser.orgaos?.[0]?.orgao?.id;
            if (defaultOrgaoId) {
                setCurrentOrgaoIdState(defaultOrgaoId);
                if (!savedOrgaoId) {
                    localStorage.setItem('current_orgao_id', String(defaultOrgaoId));
                }
            }
        }
    }, [adminUser]);

    useEffect(() => {
        loadCurrentOrgao();
    }, [loadCurrentOrgao]);

    const setCurrentOrgaoId = useCallback((orgaoId: number | null) => {
        setCurrentOrgaoIdState(orgaoId);
        if (orgaoId) {
            localStorage.setItem('current_orgao_id', String(orgaoId));
        } else {
            localStorage.removeItem('current_orgao_id');
        }
    }, []);

    return (
        <OrgaoContext.Provider value={{
            currentOrgaoId,
            setCurrentOrgaoId
        }}>
            {children}
        </OrgaoContext.Provider>
    );
}

export function useOrgao() {
    const context = useContext(OrgaoContext);

    if (!context) {
        throw new Error("useOrgao must be used within an OrgaoProvider");
    }

    return context;
}

